import React, { useState } from 'react';
import axios from 'axios';
import { CheckCircle, Upload, FileText, Calendar, User, AlertCircle, X, MessageSquare, PlayCircle } from 'lucide-react';
import { toast } from 'sonner';
import API from '../config/api';
import { formatDateTimeShortET } from '../utils/formatters';
import { useDropZone } from '../hooks/useDropZone';

/**
 * Clarification Status Block - Matches Mail-Away UI Design Exactly
 * 
 * Shows clarification status and provides quick actions for Team Leader/Admin
 * V1 REQUIREMENT: All timestamps displayed in America/New_York (ET)
 */
const ClarificationStatusBlock = ({ orderData, currentUser, onUpdate, component = null }) => {
  const [showReceivedModal, setShowReceivedModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resuming, setResuming] = useState(false);
  const [error, setError] = useState(null);
  // MLS-CLARIFICATION-CYCLE-001: component="mls" reads/writes the MLS component cycle
  // (components.mls.clarification_*) through the SAME endpoint + UI as the Tax order-level cycle.
  const isMls = component === 'mls';
  const mlsDoc = isMls ? (orderData?.components?.mls || {}) : null;
  const src = isMls ? mlsDoc : (orderData || {});
  
  // Form state
  const [responseDetails, setResponseDetails] = useState('');

  // BUG FIX (Feb 22, 2026 — Team Leader drag-drop not working):
  // Replaced inline drag handlers + file-input label pattern with the shared
  // `useDropZone` hook so click-to-upload and drag-drop follow one code path
  // (see /app/frontend/src/hooks/useDropZone.js). Validation (10MB, PDF/JPG/PNG)
  // lives inside the hook. Permissions are enforced at render time below, NOT
  // inside any handler in the hook.
  // Feb 2026 — Multi-file support: up to 3 attachments per submission,
  // 30 MB total cap, existing per-file PDF/JPG/PNG + 10 MB checks
  // preserved. Backward-compatible — first file still posts as `file=`
  // for older backends; additional files post as repeated `files=`.
  const zone = useDropZone({
    allowedTypes: ['application/pdf', 'image/jpeg', 'image/png'],
    maxSize: 10 * 1024 * 1024,
    multiple: true,
    appendMode: true,
    maxFiles: 3,
    maxTotalSize: 30 * 1024 * 1024,
  });
  // `selectedFiles` = the user's new picks for THIS submission.
  // (Don't confuse with `responseFiles` below which is the order's
  // historical list pulled from orderData.files.)
  const selectedFiles = Array.isArray(zone.file) ? zone.file : [];
  const responseFile = selectedFiles[0] || null;

  // Check if user has permission (Admin or Team Leader - both Tax and Title)
  const hasPermission = 
    currentUser?.role === 'admin' || 
    currentUser?.role === 'tax_search_team_leader' ||
    currentUser?.role === 'title_search_team_leader';
  
  // Check if clarification response has been received (matching Mail-Away pattern)
  const isResponseReceived = src?.clarification_received === true;
  
  // Get received info
  const receivedDate = src?.clarification_received_at;
  const receivedByName = src?.clarification_received_by_name;
  
  // Get latest clarification response
  const clarificationResponses = src?.clarification_responses || [];
  const latestResponse = clarificationResponses[clarificationResponses.length - 1];
  
  // Get response file
  const responseFiles = orderData?.files?.filter(f => f.file_type === 'clarification_response') || [];
  const latestResponseFile = responseFiles[responseFiles.length - 1];
  
  // Get clarification notes from status_notes (the Processor's clarification request)
  const statusNotes = orderData?.status_notes || [];
  const clarificationNotes = isMls
    ? (mlsDoc.clarification_reason || mlsDoc.clarification_note
      ? [{ message: [mlsDoc.clarification_reason, mlsDoc.clarification_note].filter(Boolean).join(' — '),
          created_at: mlsDoc.clarification_requested_at, created_by_name: mlsDoc.clarification_requested_by_name }]
      : [])
    : statusNotes.filter(note => note.type === 'clarification');
  const latestClarificationNote = clarificationNotes[clarificationNotes.length - 1];
  // Resume MLS Processing — same component endpoint the Fulfillment card uses (managers or the assigned processor)
  const canResumeMls = isMls && (
    ['admin', 'tax_search_team_leader'].includes(currentUser?.role) ||
    (['processor', 'tax_processor'].includes(currentUser?.role) && mlsDoc.assignee_id && mlsDoc.assignee_id === currentUser?.id)
  );
  const handleResumeMls = async () => {
    if (resuming) return;
    setResuming(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/orders/${orderData.id}/components/mls/clarification`, { enabled: false },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success('MLS processing resumed');
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resume MLS processing');
    } finally {
      setResuming(false);
    }
  };
  
  // Get all pending notes too (for context)
  const pendingNotes = statusNotes.filter(note => note.type === 'pending');
  const latestPendingNote = pendingNotes[pendingNotes.length - 1];
  
  // V1 REQUIREMENT: Use ET timezone for all timestamps
  const formatDate = formatDateTimeShortET;

  const handleRemoveFile = () => {
    zone.reset();
  };
  
  const handleMarkReceived = async () => {
    // Validate details
    if (!responseDetails.trim()) {
      setError('Please enter clarification details');
      return;
    }
    if (responseDetails.trim().length < 10) {
      setError('Details must be at least 10 characters');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('details', responseDetails.trim());
      if (isMls) formData.append('component', 'mls');

      // Feb 2026 — Multi-file submission (cap 3, total 30 MB).
      // Backward-compatible: first file still posts as `file=` so
      // older deployments still work. Additional files post as
      // repeated `files=` (FastAPI assembles them into a list).
      if (selectedFiles.length > 0) {
        formData.append('file', selectedFiles[0]);
        for (const f of selectedFiles) {
          formData.append('files', f);
        }
      }
      
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/orders/${orderData.id}/clarification/received`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (response.data.success) {
        // Close modal
        setShowReceivedModal(false);
        // Reset form
        setResponseDetails('');
        zone.reset();
        // Notify parent to refresh
        if (onUpdate) {
          onUpdate();
        }
      }
    } catch (err) {
      console.error('Error marking clarification received:', err);
      setError(
        err.response?.data?.detail || 
        'Failed to mark clarification as received. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };
  
  const handleViewDocument = async () => {
    if (!latestResponseFile) return;
    
    const { openAuthenticatedFile } = await import('../utils/filePreview');
    openAuthenticatedFile(
      `${API}/api/files/${latestResponseFile.id}/download`,
      latestResponseFile.filename || 'Clarification Document'
    );
  };
  
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6" data-testid={isMls ? 'mls-clarification-status-block' : 'clarification-status-block'}>
      {/* Header - Matches Mail-Away exactly */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          Clarification Status
          {isMls && <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-blue-50 text-blue-700 border border-blue-200" data-testid="mls-clarification-scope-chip">MLS Search</span>}
        </h3>
        
        {/* Status Badges - Matching Mail-Away style */}
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-800 border border-orange-300" data-testid={isMls ? 'mls-clarification-badge' : undefined}>
            <MessageSquare size={12} />
            Clarification
          </span>
          {isResponseReceived && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 border border-green-300" data-testid={isMls ? 'mls-clarification-received-badge' : undefined}>
              <CheckCircle size={12} />
              Response Received
            </span>
          )}
        </div>
      </div>
      
      {/* Processor's Clarification Request - Inline Notes (NEW FEATURE) */}
      {latestClarificationNote && (
        <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
              <AlertCircle size={16} className="text-amber-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-amber-800">
                  Clarification Requested
                </span>
                <span className="text-xs text-amber-600">
                  {formatDate(latestClarificationNote.created_at)}
                </span>
              </div>
              
              {/* Note Text */}
              <p className="text-sm text-gray-800 mb-2 whitespace-pre-wrap">
                {latestClarificationNote.message}
              </p>
              
              {/* Entered By */}
              <div className="flex items-center gap-2 text-xs text-amber-700">
                <User size={12} />
                <span className="font-medium">By:</span>
                <span>{latestClarificationNote.created_by_name || (isMls ? 'MLS processor' : 'Unknown')}</span>
                {latestClarificationNote.created_by_name?.toLowerCase().includes('processor') && (
                  <span className="px-1.5 py-0.5 bg-amber-200 text-amber-800 rounded text-xs font-medium">
                    Processor
                  </span>
                )}
              </div>
              
              {/* Show if this is a re-opened clarification (after response was received) */}
              {!isMls && isResponseReceived && clarificationNotes.length > clarificationResponses.length && (
                <div className="mt-2 pt-2 border-t border-amber-200">
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600">
                    <AlertCircle size={12} />
                    Re-opened after response
                  </span>
                </div>
              )}
            </div>
          </div>
          
          {/* Multiple Clarification Requests Indicator */}
          {clarificationNotes.length > 1 && (
            <div className="mt-3 pt-3 border-t border-amber-200">
              <p className="text-xs text-amber-700">
                Total clarification requests: <span className="font-semibold">{clarificationNotes.length}</span>
              </p>
            </div>
          )}
        </div>
      )}
      
      {/* Response Info (when received) - Matching Mail-Away exactly */}
      {isResponseReceived && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="space-y-2 text-sm">
            {/* Received Date */}
            {receivedDate && (
              <div className="flex items-center gap-2 text-gray-700">
                <Calendar size={14} className="text-green-600" />
                <span className="font-medium">Received:</span>
                <span>{formatDate(receivedDate)}</span>
              </div>
            )}
            
            {/* Received By */}
            {(receivedByName || latestResponse?.response_received_by_name) && (
              <div className="flex items-center gap-2 text-gray-700">
                <User size={14} className="text-green-600" />
                <span className="font-medium">By:</span>
                <span>{receivedByName || latestResponse?.response_received_by_name}</span>
              </div>
            )}
            
            {/* Response File */}
            {latestResponseFile && (
              <div className="flex items-center gap-2 text-gray-700">
                <FileText size={14} className="text-green-600" />
                <span className="font-medium">File:</span>
                <button
                  onClick={handleViewDocument}
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  {latestResponseFile.display_name || latestResponseFile.file_name}
                </button>
              </div>
            )}
            
            {/* Details Preview */}
            {latestResponse?.response_details && (
              <div className="mt-3 pt-3 border-t border-green-200">
                <p className="font-medium text-gray-700 mb-1">Details:</p>
                <p className="text-gray-600 text-sm">
                  {latestResponse.response_details.length > 150
                    ? `${latestResponse.response_details.substring(0, 150)}... `
                    : latestResponse.response_details}
                  {latestResponse.response_details.length > 150 && (
                    <button className="text-blue-600 hover:text-blue-800 text-xs">
                      View full details
                    </button>
                  )}
                </p>
              </div>
            )}
            
            {/* Multiple Responses Indicator */}
            {clarificationResponses.length > 1 && (
              <div className="mt-2 pt-2 border-t border-green-200">
                <p className="text-xs text-gray-600">
                  Total clarification cycles: <span className="font-semibold">{clarificationResponses.length}</span>
                </p>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Quick Action Buttons - Matching Mail-Away exactly */}
      {(hasPermission || canResumeMls) && (
        <div className="flex items-center gap-3">
          {!isResponseReceived ? (
            hasPermission && <button
              onClick={() => setShowReceivedModal(true)}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              data-testid={isMls ? 'mls-mark-response-received-btn' : 'mark-response-received-btn'}
            >
              <CheckCircle size={16} />
              Mark Response Received
            </button>
          ) : (
            <>
              {canResumeMls && (
                <button
                  onClick={handleResumeMls}
                  disabled={resuming}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                  data-testid="mls-resume-processing-btn"
                >
                  <PlayCircle size={16} />
                  {resuming ? 'Resuming…' : 'Resume MLS Processing'}
                </button>
              )}
              {latestResponseFile && (
                <button
                  onClick={handleViewDocument}
                  data-testid="clarification-view-response-btn"
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors"
                >
                  <FileText size={16} />
                  View Response
                </button>
              )}
            </>
          )}
        </div>
      )}
      
      {/* No Permission Message */}
      {!hasPermission && !canResumeMls && isResponseReceived && (
        <p className="text-sm text-gray-500 italic">
          Only Team Leaders and Admins can modify clarification status
        </p>
      )}
      
      {/* Mark Clarification Received Modal */}
      {showReceivedModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-gray-900 bg-opacity-75 transition-opacity" 
              onClick={() => !loading && setShowReceivedModal(false)} 
            />
            
            {/* Modal */}
            <div 
              className="relative bg-white rounded-xl shadow-2xl max-w-lg w-full"
              style={{ boxShadow: '0 20px 50px rgba(0, 0, 0, 0.3)' }}
            >
              {/* Header with Icon */}
              <div className="px-6 py-5 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <MessageSquare size={20} className="text-orange-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Mark Clarification Received
                  </h3>
                </div>
              </div>
              
              {/* Body */}
              <div className="px-6 py-5">
                {/* Error Message */}
                {(error || zone.error) && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                    <AlertCircle size={16} className="text-red-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-red-700">{error || zone.error}</p>
                  </div>
                )}
                
                <div className="space-y-5">
                  {/* Clarification Details Textarea */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Clarification Details <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      data-testid="clarification-received-details"
                      value={responseDetails}
                      onChange={(e) => setResponseDetails(e.target.value)}
                      placeholder="Enter clarification details received from customer..."
                      rows={6}
                      maxLength={2000}
                      className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm
                               focus:ring-2 focus:ring-blue-500 focus:border-transparent
                               placeholder:text-gray-400 text-sm resize-none transition-all"
                      style={{ minHeight: '120px' }}
                    />
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-xs text-gray-500">
                        Minimum 10 characters required
                      </p>
                      <p className="text-xs text-gray-500 font-medium">
                        {responseDetails.length} / 2000
                      </p>
                    </div>
                  </div>
                  
                  {/* File Upload Section */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Upload Supporting Document <span className="text-gray-400">(Optional)</span>
                    </label>
                    <p className="text-xs text-gray-500 mb-3">
                      Accepted formats: PDF, JPG, PNG • Maximum 10 MB per file • Up to 3 files • 30 MB total
                    </p>

                    {/* Feb 2026 — Drop-zone is always rendered so users can add more
                        files up to the cap. Each selected file is shown as a chip
                        with its own × remove button. */}
                    {selectedFiles.length < 3 && (
                      <label
                        {...zone.dropzoneProps}
                        className={`flex flex-col items-center justify-center w-full px-6 py-8
                                 border-2 ${zone.isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300'} border-dashed rounded-lg cursor-pointer
                                 hover:border-blue-500 hover:bg-blue-50 transition-all group`}
                        data-testid="clarification-dropzone"
                      >
                        <Upload size={32} className={`${zone.isDragging ? 'text-blue-500' : 'text-gray-400'} group-hover:text-blue-500 mb-2 transition-colors`} />
                        <span className={`text-sm font-medium ${zone.isDragging ? 'text-blue-600' : 'text-gray-600'} group-hover:text-blue-600 transition-colors`}>
                          {zone.isDragging
                            ? 'Drop file(s) here'
                            : selectedFiles.length === 0
                              ? 'Click to upload file(s)'
                              : `Add more files (${selectedFiles.length}/3 selected)`}
                        </span>
                        <span className="text-xs text-gray-500 mt-1">or drag and drop</span>
                        <input
                          id="clarification-file-upload"
                          {...zone.inputProps}
                          data-testid="clarification-file-input"
                        />
                      </label>
                    )}

                    {selectedFiles.length > 0 && (
                      <div className={`space-y-2 ${selectedFiles.length < 3 ? 'mt-3' : ''}`} data-testid="clarification-selected-files">
                        {selectedFiles.map((f, idx) => (
                          <div
                            key={`${f.name}-${idx}-${f.size}`}
                            className="flex items-center gap-3 p-3 bg-blue-50 border-2 border-blue-200 rounded-lg"
                            data-testid={`clarification-selected-file-${idx}`}
                          >
                            <div className="flex-shrink-0 w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center">
                              <FileText size={18} className="text-blue-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{f.name}</p>
                              <p className="text-xs text-gray-500">{(f.size / 1024 / 1024).toFixed(2)} MB</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => zone.removeAt(idx)}
                              className="flex-shrink-0 p-1.5 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                              title="Remove file"
                              data-testid={`clarification-remove-file-${idx}`}
                            >
                              <X size={18} />
                            </button>
                          </div>
                        ))}
                        <p className="text-xs text-gray-500 text-right" data-testid="clarification-total-size">
                          Total: {(selectedFiles.reduce((s, f) => s + (f.size || 0), 0) / 1024 / 1024).toFixed(2)} MB / 30.00 MB
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Footer */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-xl">
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleMarkReceived}
                    data-testid="clarification-received-save-btn"
                    disabled={loading || !responseDetails.trim() || responseDetails.trim().length < 10}
                    className="flex-1 px-4 py-2.5 bg-blue-600 text-white font-medium rounded-lg 
                             hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed 
                             transition-colors shadow-sm"
                  >
                    {loading ? 'Saving...' : 'Save & Mark Received'}
                  </button>
                  <button
                    onClick={() => setShowReceivedModal(false)}
                    disabled={loading}
                    className="px-4 py-2.5 bg-gray-100 text-gray-700 font-medium rounded-lg 
                             hover:bg-gray-200 disabled:cursor-not-allowed transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClarificationStatusBlock;
